import { aboutContent, siteInfo } from '@/mocks/siteData';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useState } from 'react';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    formData.delete('phone_alt');

    try {
      const resp = await fetch('https://readdy.ai/api/form/d9sqsqp4ne17ei0sdmcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="mt-4 text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              关于我们
            </h1>
            <p className="mt-2 text-sm text-foreground-500 font-body">
              了解po18阅读平台的品牌故事与发展历程
            </p>
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 items-start">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  {aboutContent.intro.title}
                </h2>
                <div className="mt-6 flex flex-col gap-4">
                  {aboutContent.intro.paragraphs.map((p, i) => (
                    <p key={i} className="text-foreground-700 font-body leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
              <div className="lg:w-80 shrink-0 bg-background-100 rounded-xl p-6">
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4">平台信息</h3>
                <div className="flex flex-col gap-3 text-sm font-body">
                  <div className="flex justify-between">
                    <span className="text-foreground-500">成立时间</span>
                    <span className="text-foreground-800 font-medium">{siteInfo.foundedYear}年</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-500">官方域名</span>
                    <span className="text-foreground-800 font-medium">{siteInfo.officialUrl.replace('https://', '')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-500">联系邮箱</span>
                    <span className="text-foreground-800 font-medium">{siteInfo.contactEmail}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-500">服务时间</span>
                    <span className="text-foreground-800 font-medium">7×24小时</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-4">
              {aboutContent.reputation.title}
            </h2>
            <p className="text-center text-foreground-500 font-body mb-10 max-w-xl mx-auto">
              来自po18平台真实用户的使用评价与反馈
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
              {aboutContent.reputation.stats.map((stat) => (
                <div key={stat.label} className="bg-background-50 rounded-xl p-4 text-center border border-background-200/70">
                  <div className="text-xl md:text-2xl font-heading font-bold text-primary-600">{stat.value}</div>
                  <div className="mt-1 text-xs text-foreground-500 font-label">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {aboutContent.reputation.items.map((item) => (
                <div key={item.name} className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < item.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 font-body leading-relaxed">{item.text}</p>
                  <p className="mt-3 text-xs text-foreground-500 font-label">— {item.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">联系我们</h2>
              <p className="mt-2 text-foreground-500 font-body">如有任何问题或建议，欢迎通过以下方式联系我们</p>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form="" id="contact-form">
              <div className="flex flex-col gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-label text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-900 font-body"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-label text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-900 font-body"
                    placeholder="请输入您的邮箱地址"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-label text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-900 font-body resize-none"
                    placeholder="请描述您的问题或建议（限500字）"
                  ></textarea>
                  <p className="mt-1 text-xs text-foreground-400">限500字以内</p>
                </div>
                <div className="spam-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-white text-sm font-label font-semibold rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  提交留言
                </button>
                {formStatus === 'success' && (
                  <div className="p-3 bg-accent-100 text-accent-700 text-sm font-body rounded-lg text-center">
                    感谢您的留言，我们会尽快回复！
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-3 bg-red-50 text-red-600 text-sm font-body rounded-lg text-center">
                    {formError}
                  </div>
                )}
              </div>
            </form>
          </div>
        </section>
      </main>
    </>
  );
}